import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";
import type { ChartProps } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartDataset,
  LegendItem,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import { isEqual } from "lodash";
import { DriverPushLapEntry } from "../../../types/fpAnalysis";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  ChartDataLabels,
  annotationPlugin,
);

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

// Colors for loops
const LOOP_COLORS = [
  "#E6194B",
  "#3CB44B",
  "#FFE119",
  "#4363D8",
  "#F58231",
  "#BB66FF",
  "#46F0F0",
  "#FF66FF",
];

// Chart data type that supports multi-line labels (arrays)
type LineChartData = ChartProps<"line">["data"] & { labelColors?: string[] };

export type LapTimeMode = "ultimate" | "median" | "fastest";

function DriverLoopsPlot({
  driverAllPushLaps,
  powerModeLabel,
  useCombinedLoops = true,
  lapTimeMode = "fastest",
}: {
  driverAllPushLaps: Record<string, DriverPushLapEntry[]>;
  powerModeLabel: string;
  useCombinedLoops?: boolean;
  lapTimeMode?: LapTimeMode;
}) {
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  const driverSelectedLap = useMemo(() => {
    const result: Record<string, DriverPushLapEntry> = {};

    Object.entries(driverAllPushLaps || {}).forEach(([driver, laps]) => {
      if (laps && laps.length > 0) {
        if (lapTimeMode === "fastest") {
          // Find the lap with the minimum lapTime
          const fastestLap = laps.reduce((fastest, current) =>
            current.lapTime < fastest.lapTime ? current : fastest,
          );
          result[driver] = fastestLap;
        } else if (lapTimeMode === "median") {
          // Find the median lap (middle lap when sorted by lap time)
          const sortedLaps = [...laps].sort((a, b) => a.lapTime - b.lapTime);
          const midIndex = Math.floor(sortedLaps.length / 2);
          // For even count, take the slower of the two middle laps
          result[driver] = sortedLaps[midIndex];
        } else if (lapTimeMode === "ultimate") {
          // Build ultimate lap: best per-loop (delta) time for each loop across all laps
          // Then rebuild cumulative times from those best deltas
          const bestPerLoopTimes: Record<string, number> = {};

          laps.forEach((lap) => {
            const loopKeys = Object.keys(lap.loops)
              .map((k) => parseInt(k, 10))
              .sort((a, b) => a - b);

            let prevCumulative = 0;
            loopKeys.forEach((loopKey) => {
              const cumulative = lap.loops[String(loopKey)];
              const perLoopTime = cumulative - prevCumulative;
              const key = String(loopKey);

              if (
                bestPerLoopTimes[key] === undefined ||
                perLoopTime < bestPerLoopTimes[key]
              ) {
                bestPerLoopTimes[key] = perLoopTime;
              }
              prevCumulative = cumulative;
            });
          });

          // Rebuild cumulative times from best per-loop times
          const ultimateLoops: Record<string, number> = {};
          const sortedLoopKeys = Object.keys(bestPerLoopTimes)
            .map((k) => parseInt(k, 10))
            .sort((a, b) => a - b);

          let cumulative = 0;
          sortedLoopKeys.forEach((loopKey) => {
            cumulative += bestPerLoopTimes[String(loopKey)];
            ultimateLoops[String(loopKey)] = cumulative;
          });

          const ultimateLapTime =
            sortedLoopKeys.length > 0
              ? ultimateLoops[String(sortedLoopKeys[sortedLoopKeys.length - 1])]
              : 0;

          result[driver] = {
            lapNumber: -1, // Virtual lap
            lapTime: ultimateLapTime,
            loops: ultimateLoops,
          };
        }
      }
    });

    return result;
  }, [driverAllPushLaps, lapTimeMode]);

  const chartData: LineChartData = useMemo(() => {
    // Get all loop indices from timingLoops, with loop 0 (FL) at the end
    const loopIndices = timingLoops
      ? Object.keys(timingLoops)
          .map((k) => parseInt(k, 10))
          .filter((n) => !isNaN(n))
          .sort((a, b) => {
            // Loop 0 (FL) goes last
            if (a === 0) return 1;
            if (b === 0) return -1;
            return a - b;
          })
          .map(String)
      : [];

    if (loopIndices.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Build a map from loop name to data key
    const loopNameToDataKey: Record<string, string> = {};
    loopIndices.forEach((loopIdx) => {
      const loopInfo = timingLoops?.[loopIdx as `${number}`];
      const loopName = loopInfo?.name;
      if (loopName) {
        // Loop 0 (FL) maps to data key "8", others use their index
        loopNameToDataKey[loopName] = loopIdx === "0" ? "8" : loopIdx;
      }
    });

    // Get combined loop configuration from strategy import
    const combinedLoops: { label: string; loopNames: string[] }[] = [];
    for (let i = 1; i <= 8; i++) {
      const loopNamesArray = strategyImport?.[`combinedLoop${i}`] as
        | (string | null)[]
        | undefined;
      const label = strategyImport?.[`combinedLoopLabel${i}`] as
        | string
        | undefined;

      if (label && loopNamesArray) {
        // Filter out null values from the loop names array
        const validLoopNames = loopNamesArray.filter(
          (name): name is string => name !== null && name !== undefined,
        );
        if (validLoopNames.length > 0) {
          combinedLoops.push({ label, loopNames: validLoopNames });
        }
      }
    }

    // Use combined loops if enabled via prop AND config exists
    const shouldUseCombinedLoops = useCombinedLoops && combinedLoops.length > 0;

    // Map timing loop index to data key (loop 0 in timing = key "8" in data)
    const getDataKey = (timingLoopIdx: string) =>
      timingLoopIdx === "0" ? "8" : timingLoopIdx;

    // Convert cumulative times to per-loop times for each driver
    const driverPerLoopTimes: Record<string, Record<string, number>> = {};
    Object.entries(driverSelectedLap).forEach(([driverKey, entry]) => {
      driverPerLoopTimes[driverKey] = {};
      let prevCumulativeTime = 0;
      loopIndices.forEach((loop) => {
        const dataKey = getDataKey(loop);
        const cumulativeTime = entry.loops[dataKey];
        if (cumulativeTime !== undefined) {
          // Per-loop time = current cumulative - previous cumulative
          driverPerLoopTimes[driverKey][dataKey] =
            cumulativeTime - prevCumulativeTime;
          prevCumulativeTime = cumulativeTime;
        }
      });
    });

    // Calculate combined loop times for each driver
    const driverCombinedLoopTimes: Record<string, Record<number, number>> = {};
    if (shouldUseCombinedLoops) {
      Object.entries(driverPerLoopTimes).forEach(
        ([driverKey, perLoopTimes]) => {
          driverCombinedLoopTimes[driverKey] = {};
          combinedLoops.forEach((combined, idx) => {
            let totalTime = 0;
            let hasAllLoops = true;
            for (const loopName of combined.loopNames) {
              const dataKey = loopNameToDataKey[loopName];
              if (dataKey && perLoopTimes[dataKey] !== undefined) {
                totalTime += perLoopTimes[dataKey];
              } else {
                hasAllLoops = false;
                break;
              }
            }
            if (hasAllLoops) {
              driverCombinedLoopTimes[driverKey][idx] = totalTime;
            }
          });
        },
      );
    }

    // Figure out fastest time for each combined loop (or individual loop)
    const fastestTimes: Record<number, number> = {};
    if (shouldUseCombinedLoops) {
      Object.values(driverCombinedLoopTimes).forEach((times) => {
        Object.entries(times).forEach(([idxStr, time]) => {
          const idx = parseInt(idxStr, 10);
          if (fastestTimes[idx] === undefined || time < fastestTimes[idx]) {
            fastestTimes[idx] = time;
          }
        });
      });
    } else {
      Object.values(driverPerLoopTimes).forEach((loops) => {
        Object.entries(loops).forEach(([loop, time]) => {
          const idx = parseInt(loop, 10);
          if (fastestTimes[idx] === undefined || time < fastestTimes[idx]) {
            fastestTimes[idx] = time;
          }
        });
      });
    }

    // Create a dataset for each driver with their deltas
    const jagDrivers: ChartDataset<"line">[] = [];
    const nonJagDrivers: ChartDataset<"line">[] = [];

    // Convert line style string to borderDash array
    const getLineDash = (lineStyle: string | undefined): number[] => {
      if (lineStyle?.toLowerCase() === "dash") return [10, 5];
      return [];
    };

    // Get labels for x-axis (with composition on second line for combined loops)
    const labels: (string | string[])[] = shouldUseCombinedLoops
      ? combinedLoops.map((c) => [c.label, c.loopNames.join("+")])
      : loopIndices.map((loopIdx) => {
          const loopInfo = timingLoops?.[loopIdx as `${number}`];
          return loopInfo?.name || `Loop ${loopIdx}`;
        });

    // Get colors for each label
    const labelColors = labels.map(
      (_, idx) => LOOP_COLORS[idx % LOOP_COLORS.length],
    );

    Object.entries(driverSelectedLap).forEach(([driverKey, _]) => {
      const info = driverInfo?.[driverKey];
      const driverName = info?.driverName || driverKey;
      const driverColor = info?.driverTeamColour || "#888888";
      const isJaguar =
        info?.driverTeam?.toLowerCase().includes("jaguar") ?? false;
      const lineDash = isJaguar ? getLineDash(info?.driverLineStyle) : [];

      // Calculate delta for each combined loop (or individual loop)
      let deltas: (number | null)[];
      if (shouldUseCombinedLoops) {
        deltas = combinedLoops.map((_, idx) => {
          const driverTime = driverCombinedLoopTimes[driverKey]?.[idx];
          const fastestTime = fastestTimes[idx];
          if (driverTime === undefined || fastestTime === undefined) {
            return null;
          }
          // Convert from ms to seconds
          return (driverTime - fastestTime) / 1000;
        });
      } else {
        deltas = loopIndices.map((loop) => {
          const dataKey = getDataKey(loop);
          const driverTime = driverPerLoopTimes[driverKey]?.[dataKey];
          const idx = parseInt(dataKey, 10);
          const fastestTime = fastestTimes[idx];
          if (driverTime === undefined || fastestTime === undefined) {
            return null;
          }
          // Convert from ms to seconds
          return (driverTime - fastestTime) / 1000;
        });
      }

      const dataset: ChartDataset<"line"> = {
        label: driverName,
        data: deltas,
        borderColor: driverColor,
        backgroundColor: driverColor,
        borderWidth: 6,
        pointRadius: 5,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 1,
        showLine: isJaguar,
        order: isJaguar ? 0 : 1,
        borderDash: lineDash,
        clip: { top: 0, bottom: 10, left: 0, right: 0 },
      };

      if (isJaguar) {
        jagDrivers.push(dataset);
      } else {
        nonJagDrivers.push(dataset);
      }
    });

    // Non-Jag first (bottom), then Jag (top)
    const datasets = [...nonJagDrivers, ...jagDrivers];

    return { labels, datasets, labelColors };
  }, [
    driverSelectedLap,
    driverInfo,
    timingLoops,
    strategyImport,
    useCombinedLoops,
  ]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: true,
          position: "bottom" as const,
          labels: {
            color: "#ffffff",
            font: {
              weight: "bold" as const,
            },
            usePointStyle: true,
            generateLabels: (chart: ChartJS): LegendItem[] => {
              const datasets = chart.data.datasets;
              // Only show Jaguar drivers (those with showLine: true)
              return datasets
                .filter(
                  (dataset) => (dataset as { showLine?: boolean }).showLine,
                )
                .map((dataset, _) => {
                  const originalIndex = datasets.indexOf(dataset);
                  return {
                    text: dataset.label ?? "",
                    fontColor: "#ffffff",
                    strokeStyle: dataset.borderColor as string,
                    fillStyle: dataset.borderColor as string,
                    lineWidth: 3,
                    lineDash:
                      (dataset as { borderDash?: number[] }).borderDash ?? [],
                    hidden: !chart.isDatasetVisible(originalIndex),
                    datasetIndex: originalIndex,
                    pointStyle: "line" as const,
                  };
                });
            },
          },
        },
        title: {
          display: true,
          text: `${
            lapTimeMode.charAt(0).toUpperCase() + lapTimeMode.slice(1)
          } Lap Loop Times - ${powerModeLabel}`,
          color: "#ffffff",
          font: { size: 18, weight: "bold" as const },
        },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<"line">) => {
              const delta = context.raw as number;
              if (delta === null || delta === undefined) return "";
              return `${context.dataset.label}: +${delta.toFixed(3)}s`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "category" as const,
          position: "bottom" as const,
          offset: true,
          title: {
            display: true,
            text: "Loop",
            color: "#ffffff",
          },
          ticks: {
            color: (context: { index: number }) =>
              chartData.labelColors?.[context.index] || "#ffffff",
            font: {
              size: 12,
              weight: "bold" as const,
            },
          },
          grid: { color: "rgba(255, 255, 255, 0.1)" },
        },
        y: {
          type: "linear" as const,
          max: 0.5,
          title: {
            display: true,
            text: "Delta to Fastest (s)",
            color: "#ffffff",
          },
          ticks: {
            color: "#ffffff",
            callback: (value: string | number) => {
              const numValue =
                typeof value === "string" ? parseFloat(value) : value;
              return `+${numValue.toFixed(2)}s`;
            },
          },
          grid: { color: "rgba(255, 255, 255, 0.1)" },
        },
      },
    }),
    [powerModeLabel, lapTimeMode, chartData],
  );

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default DriverLoopsPlot;
